import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import urlImage from "../assets/images/auth-image.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState('');
  const [validationError, setValidationError] = useState('');
  const [linkError, setLinkError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email') ? decodeURIComponent(searchParams.get('email')) : null;

  useEffect(() => {
    if (token && email) {
      setStatus('pending');
      setLinkError(false);
    } else {
      setStatus('pending');
      setLinkError(true);
      setMessage('Invalid reset link. Please request a new password reset.');
    }
  }, [token, email]);

  useEffect(() => {
    if (!password || !confirmPassword) {
      setValidationError('');
      return;
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
    } else {
      setValidationError('');
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setValidationError('');

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    try {
      await authService.confirmPasswordReset({
        token,
        email,
        newPassword: password,
        confirmPassword: password
      });

      setStatus('success');
      setMessage('Password has been reset successfully!');
      
      setTimeout(() => {
        navigate('/auth/login');
      }, 3000);
    } catch (err) {
      const apiMessage = err.response?.data?.message || 'Failed to reset password. Please try again.';
      const normalizedMessage = apiMessage.toLowerCase();
      const isResetLinkError =
        normalizedMessage.includes('invalid reset link') ||
        normalizedMessage.includes('invalid token') ||
        normalizedMessage.includes('token expired') ||
        normalizedMessage.includes('expired token');

      if (isResetLinkError) {
        setLinkError(true);
      }

      setMessage(apiMessage);
    }
  };

  return (
    <Container fluid className="vh-100">
      <Row className="h-100 justify-content-center">
        <Col lg={4} md={12} sm={12} className="d-none d-md-block">
          <div 
            className="h-100 w-100" 
            style={{
              backgroundImage: `url(${urlImage})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          />
        </Col>
        <Col lg={6} md={12} sm={12} className="d-flex justify-content-center align-items-center">
          <Card className="p-4 shadow br-radius-12" style={{ width: "350px" }}>
            <Card.Body>
              <h5 className="GeneralSansSemibold text-center pt-3 fs-20">Reset Password</h5>
              <p className="text-center mb-4 fs-14">Enter your new password</p>
              
              {linkError ? (
                <div className="text-center">
                  <Alert variant="danger">{message}</Alert>
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/auth/forgot-password')}
                    className="mt-3"
                  >
                    Request New Reset Link
                  </Button>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {message && (
                    <Alert variant={status === 'success' ? 'success' : 'danger'}>
                      {message}
                    </Alert>
                  )}

                  {validationError && (
                    <Alert variant="danger" className="mb-3">
                      {validationError}
                    </Alert>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label className="fs-12">New Password</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        required
                        disabled={status === 'success'}
                        className="pe-5"
                      />
                      <button
                        type="button"
                        className="position-absolute top-50 end-3 translate-middle-y fs-12 text-success"
                        style={{ right: '10px', zIndex: 10, border: 'none', background: 'transparent' }}
                        onClick={() => setShowPassword(prev => !prev)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fs-12">Confirm Password</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                        disabled={status === 'success'}
                        className="pe-5"
                      />
                      <button
                        type="button"
                        className="position-absolute top-50 end-3 translate-middle-y fs-12 text-success"
                        style={{ right: '10px', zIndex: 10, border: 'none', background: 'transparent' }}
                        onClick={() => setShowConfirmPassword(prev => !prev)}
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      >
                        <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </Form.Group>

                  <div className="d-grid">
                    <Button 
                      variant="success" 
                      type="submit"
                      disabled={status === 'success' || Boolean(validationError)}
                      className="w-100"
                    >
                      Reset Password
                    </Button>
                  </div>

                  <div className="text-center mt-3">
                    <a 
                      href="/auth/login"
                      className="text-dark fs-12"
                    >
                      Back to Login
                    </a>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
